import { createTheme } from '@mui/material/styles';

// Brand palette for the redesigned public site. We lean on a deep warm navy,
// a cream "paper" background, and a muted brass accent so headings and CTAs
// read as editorial rather than corporate. Kept as named exports so other
// files (StyledButton, page-level overrides) can reuse the exact tokens.
export const brand = {
  navy: '#10243E',
  navyHover: '#1B3A60',
  cream: '#FBF7F1',
  paper: '#FFFFFF',
  brass: '#B0894C',
  brassHover: '#946F36',
  textPrimary: '#1B1B1B',
  textSecondary: '#5A5A5A',
  hairline: '#E5DED2',
};

const serif = '"Fraunces", "Playfair Display", Georgia, "Times New Roman", serif';
const sans = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const theme = createTheme({
  palette: {
    primary: {
      main: brand.navy,
      dark: brand.navyHover,
      contrastText: '#ffffff',
    },
    secondary: {
      main: brand.brass,
      dark: brand.brassHover,
      contrastText: '#ffffff',
    },
    background: {
      default: brand.paper,
      paper: brand.paper,
    },
    text: {
      primary: brand.textPrimary,
      secondary: brand.textSecondary,
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: sans,
    htmlFontSize: 16,
    // Older audience -> bump body to 18px and lead text to 20px so reading
    // is comfortable without zoom. Headings use the editorial serif.
    fontSize: 18,
    h1: {
      fontFamily: serif,
      fontWeight: 600,
      fontSize: 'clamp(2.5rem, 5vw, 4rem)',
      lineHeight: 1.05,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontFamily: serif,
      fontWeight: 600,
      fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
      lineHeight: 1.1,
      letterSpacing: '-0.005em',
    },
    h3: {
      fontFamily: serif,
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: serif,
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: sans,
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.3,
    },
    h6: {
      fontFamily: sans,
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.35,
    },
    subtitle1: {
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1.125rem',
      lineHeight: 1.65,
    },
    body2: {
      fontSize: '1rem',
      lineHeight: 1.55,
    },
    button: {
      fontFamily: sans,
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '0.01em',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.paper,
          color: brand.textPrimary,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        // Tabular figures keep the countdown numerals aligned as they tick.
        '.tabular-nums': {
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum"',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 24,
          paddingRight: 24,
          transition: 'background-color 200ms ease-out, box-shadow 200ms ease-out, transform 200ms ease-out',
        },
        sizeLarge: {
          minHeight: 56,
          fontSize: '1.0625rem',
          paddingLeft: 32,
          paddingRight: 32,
        },
        sizeMedium: {
          minHeight: 48,
        },
        containedPrimary: {
          backgroundColor: brand.navy,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: brand.navyHover,
            boxShadow: '0 8px 24px rgba(16, 36, 62, 0.18)',
          },
        },
        outlinedPrimary: {
          borderColor: brand.navy,
          color: brand.navy,
          borderWidth: 1.5,
          '&:hover': {
            borderColor: brand.navyHover,
            borderWidth: 1.5,
            backgroundColor: 'rgba(16, 36, 62, 0.04)',
          },
        },
        textPrimary: {
          color: brand.navy,
          '&:hover': {
            backgroundColor: 'rgba(16, 36, 62, 0.04)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: brand.paper,
          color: brand.textPrimary,
          boxShadow: 'none',
          borderBottom: `1px solid ${brand.hairline}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 16,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.95rem',
          height: 36,
        },
      },
    },
  },
});

export default theme;
